import { useAppStore, useUserStore } from '@/stores'
import { usePermissionsMachine } from '@/state'
import { devicesShortNamesMap, deviceTypesMap } from '@browser'
import {messenger} from '@communication'
import {androidConnector} from '@communication/remotes/AndroidConnector'
import {localStream} from '@stream'

// Permission/event names arrive either as spec names ('camera'/'microphone' — what
// PermissionStatus.name returns in current Chrome/Edge/Firefox) or as the legacy
// Chromium names ('video_capture'/'audio_capture' — still used by messenger events).
const permissionDevicesMap = new Map([
  ['video_capture', 'camera'],
  ['audio_capture', 'microphone'],
  ['camera', 'camera'],
  ['microphone', 'microphone'],
])

export default class PermissionsProcedure {
  constructor () {
    const app = useAppStore()
    this.name = 'permissionsProcedure'
    this.queue = []
    this.timer = null
    this.permissionStatuses = []
    this.recovering = {}
    this.endedTracks = {}
    this.mutedBeforeLoss = {}
    this.recommendedDevices = [
      ...(app.isCameraRecommended ? ['camera'] : []),
      ...(app.isMicrophoneRecommended ? ['microphone'] : []),
    ]
  }

  install = async () => {
    // Track-based fallback stays active everywhere: the Permissions API path only
    // reacts to permission changes, not to tracks ending (device unplugged/claimed).
    messenger.subscribe(this.name, 'stream:track:end', this.permissionDeniedHandler)
    messenger.subscribe(this.name, 'stream:track:start', this.permissionGrantedHandler)
    document.addEventListener('visibilitychange', this.visibilityHandler)

    this.addPermissionsListeners(this.recommendedDevices)
      .catch((error) => {
        // permissions.query does not support camera/microphone here (Firefox < 131)
        console.error(error)
      })
  }

  // Back on the page, a kind that is still missing is tried again on the join and meeting views: lost while the page was
  // hidden (another app took the camera, a phone call the microphone), a recovery that failed meanwhile, or never
  // acquired. Only when the browser allows it without asking: a 'prompt' would ask on every return (Chrome blocks the
  // site after 3 dismissals), and 'denied' cannot succeed. Not queryable (Firefox < 131): tried, as on the other paths.
  // Before, a client whose camera was missing was taken out of the session on its next return to the page
  visibilityHandler = async () => {
    if (document.visibilityState !== 'visible') return
    if (!['app.view.join', 'app.view.meeting'].some((view) => useAppStore().state.matches(view))) return
    for (const device of this.recommendedDevices) {
      if (useUserStore().profile[device] !== 'not-available') continue
      let state
      try {
        state = (await navigator.permissions.query({ name: device })).state
      } catch (error) {
        // not queryable
      }
      if (state === undefined || state === 'granted') this.updateStream(devicesShortNamesMap.get(device))
    }
  }

  addPermissionsListeners = async (devices) => {
    for (const device of devices) {
      const permission = await navigator.permissions.query({ name: device })
      // Keep a reference so the status object (and its onchange) is not GC'd
      this.permissionStatuses.push(permission)
      let previousState = permission.state
      permission.onchange = () => {
        const wasDenied = previousState === 'denied'
        previousState = permission.state
        if (permission.state === 'granted') {
          // prompt -> granted comes from the getUserMedia call that showed the prompt, which installs its own track
          // and state — unless the kind is still missing (prompt dismissed, then allowed via the lock icon), also on
          // the permissions view once it is not requesting the devices itself ('Continue' would join without them)
          const requesting = useAppStore().state.matches('app.view.permissions') &&
            ['ui.init', 'ui.media', 'ui.getMediaStreams'].some((ui) => usePermissionsMachine().state.value.matches(ui))
          const missing = useUserStore().profile[device] === 'not-available' && !requesting &&
            ['app.view.permissions', 'app.view.join', 'app.view.meeting'].some((view) => useAppStore().state.matches(view))
          if (wasDenied || missing) this.updateStream(devicesShortNamesMap.get(device))
        } else {
          // Blocked: stop the capture even if the browser keeps it running (Android bridge tracks are not captures)
          const track = localStream.getTrackByType(devicesShortNamesMap.get(device))
          if (permission.state === 'denied' && localStream.isCapture(track)) track.stop()
          this.permissionDeniedHandler(permission)
        }
      }
    }
  }

  permissionDeniedHandler = (permission) => {
    const device = permissionDevicesMap.get(permission?.name)
    if (!device) return

    const user = useUserStore()
    const app = useAppStore()
    // Recovery re-applies the user's mute choice once the kind has a live track again
    if (user.profile[device] !== 'not-available') this.mutedBeforeLoss[device] = user.profile[device] === 'muted'

    app.transition(`${device.toUpperCase()}_NOT_AVAILABLE`)
    user.profile[device] = 'not-available'
    user.profileChanged()
    this.queue.push(permission)

    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      // Recover every kind queued within the debounce window, not only the last one
      this.queue.forEach((queued) => this.updateStream(devicesShortNamesMap.get(permissionDevicesMap.get(queued.name))))
      this.queue = []
    }, 1000)
  }

  permissionGrantedHandler = (permission, options) => {
    const device = permissionDevicesMap.get(permission?.name)
    if (!device) return

    const app = useAppStore()
    const user = useUserStore()
    const type = devicesShortNamesMap.get(device)

    app.transition(`${device.toUpperCase()}_AVAILABLE`)
    user.profile[device] = 'on'
    user.profileChanged()
    if (options?.permissionsApi) this.updateStream(type)
  }

  updateStream = (type) => {
    // Single flight per kind: the 'ended' fallback and the camera/microphone onchange
    // handlers can overlap and must not capture the same kind twice
    if (!this.recovering[type]) {
      this.recovering[type] = this.recoverTrack(type).finally(() => {
        this.recovering[type] = null
      })
    }
    return this.recovering[type]
  }

  recoverTrack = async (type) => {
    const device = deviceTypesMap.get(type)
    if (!this.recommendedDevices.includes(device) || useAppStore().state.matches('app.view.exit')) return
    const oldTrack = localStream.getTrackByType(type)
    // A live track (e.g. from MeetingView's devicechange replaceDevice) is never touched, only the state restored
    if (oldTrack?.readyState === 'live') return this.restoreState(device, oldTrack)
    // While the Android bridge is connected only a lost browser capture is recovered: its tracks (e.g. smart glasses)
    // are not browser captures and are never replaced by one
    if (androidConnector.connected && !localStream.isCapture(oldTrack ?? this.endedTracks[type])) return
    // As before, the kind is left empty while it is re-acquired (SFU cannot produce an ended track); the ended track
    // is kept, also across a failed attempt, to hand the P2P sender / SFU producer over to the new track
    if (oldTrack) localStream[type].removeTrack(oldTrack)
    this.endedTracks[type] = oldTrack ?? this.endedTracks[type]
    try {
      const stream = await this.requestWithBackoff(device)
      const track = stream.getTracks()[0]
      const current = localStream.getTrackByType(type)
      // Another flow (PermissionsView, a device switch) installed a track meanwhile, or the call ended
      if (current || useAppStore().state.matches('app.view.exit')) {
        track.stop()
        if (current?.readyState === 'live') this.restoreState(device, current)
        return
      }
      const muted = useUserStore().profile[device] === 'muted' || !!this.mutedBeforeLoss[device]
      localStream.addExistingTrack(device, track, { muted: { [type]: muted }, oldTrack: this.endedTracks[type] })
      this.endedTracks[type] = null
      this.restoreState(device, track)
    } catch (error) {
      console.error('error', device, error)
    }
  }

  // A camera that is not released yet (another app, or the snapshot's stop-and-reopen on a slow phone) fails with
  // NotReadableError/AbortError for a while: try again a few times before the kind stays 'not-available'
  requestWithBackoff = async (device, delays = [1000, 2000]) => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await localStream.requestStream(device)
      } catch (error) {
        const retriable = error?.name === 'NotReadableError' || error?.name === 'AbortError'
        if (!retriable || attempt >= delays.length || useAppStore().state.matches('app.view.exit')) throw error
        await new Promise((resolve) => setTimeout(resolve, delays[attempt]))
      }
    }
  }

  // Undo 'not-available' for a kind with a live track and re-apply the mute that 'stream:track:start' reset
  restoreState = (device, track) => {
    if (useUserStore().profile[device] === 'not-available') this.permissionGrantedHandler({ name: device })
    if (useUserStore().profile[device] === 'on' && (this.mutedBeforeLoss[device] || !track.enabled)) {
      useAppStore().transition(`MUTE_${device.toUpperCase()}`, { isSilent: true })
    }
    this.mutedBeforeLoss[device] = false
  }

  uninstall = () => {
    messenger.unsubscribe(this.name)
    document.removeEventListener('visibilitychange', this.visibilityHandler)
    this.permissionStatuses.forEach((permission) => {
      permission.onchange = null
    })
    this.permissionStatuses = []
    if (this.timer) clearTimeout(this.timer)
  }

}
